/* eslint-disable no-unused-vars */
import React from 'lib/react';
import Typography from 'commonComponents/typography';
import { useTranslation } from 'lib/translation';
import {
  EVGrid,
  EVLinearProgress,
  EVInsertChartOutlinedIcon,
} from 'lib/components';
import { getCurrencySymbol } from 'features/order/Order.utils';
import { renderSubscriptionIconWithOne } from 'features/order/SubscriptionIcon.utils';
import { Link } from 'lib/router';
import PropTypes from 'prop-types';
import { ORDER_HISTORY_ROUTE, CONSUMPTION_ROUTE } from 'constants/RouteConstants';
import useMediaQuery from 'hooks/useMediaQuery';
import { formatDateForDisplay } from '../subscriptionDashboard/SubscriptionDashboard.utils';
import SubscriptionCardStyles from './SubscriptionCardStyles';

// Calculate overages
const calculateOverage = (subscription) => {
  if (subscription.usedQty > subscription.includedQty) {
    return subscription.usedQty - subscription.includedQty;
  }
  return 0;
};

function SubscriptionCard({ subscription }) {
  const classes = SubscriptionCardStyles();
  const overage = calculateOverage(subscription);
  const isOverLimit = overage > 0;
  const isMobile = useMediaQuery(640);
  const { t } = useTranslation();

  return (
    <EVGrid className={classes.subscriptionCard}>
      <EVGrid className={classes.subscriptionCardContent}>
        {renderSubscriptionIconWithOne({
          subscriptionSkuName: subscription.subscriptionSkuName,
          iconClassName: classes.iconStyle,
          containerClassName: classes.subscriptionIconContainer,
          badgeClassName: classes.subscriptionIcon,
          badgeTextClassName: classes.subscriptionIconText,
        })}

        <EVGrid className={classes.subscriptionDetails}>

          {/* Subscription Icon/Header */}
          <EVGrid className={classes.subscriptionHeader}>
            <Typography variant="body-600" fontColor="charcoal-900" className={classes.subscriptionName}>
              {subscription.subscriptionSkuName}
            </Typography>
          </EVGrid>

          <EVGrid className={classes.subscriptionInfo}>
            {/* Price */}
            <EVGrid className={classes.priceContainer}>
              <Typography className={classes.priceText}>
                {getCurrencySymbol(1)}
                {subscription.monthlyFee.toLocaleString()}
                {isMobile ? t('subscription_card__per_month_short') : t('subscription_card__per_month')}
              </Typography>
            </EVGrid>
            <EVGrid className={classes.datesAndOrderHistoryContainer}>
              {/* Dates */}
              <EVGrid className={classes.datesContainer}>
                <EVGrid className={classes.dateRow}>
                  <Typography className={classes.dateLabel}>
                    {t('subscription_card__effective_date')}
                  </Typography>
                  <Typography className={classes.dateValue}>
                    {formatDateForDisplay(subscription.startDate)}
                  </Typography>
                </EVGrid>
                <EVGrid className={classes.dateRow}>
                  <Typography className={classes.dateLabel}>
                    {t('subscription_card__end_date')}
                  </Typography>
                  <Typography className={classes.dateValue}>
                    {formatDateForDisplay(subscription.endDate)}
                  </Typography>
                </EVGrid>
              </EVGrid>
              <Link to={CONSUMPTION_ROUTE}>
                <EVInsertChartOutlinedIcon htmlColor="#007CAD" style={{ fontSize: '36px' }} />
              </Link>
            </EVGrid>
          </EVGrid>
        </EVGrid>
      </EVGrid>

      {/* Consumption */}
      <EVGrid className={classes.consumptionContainer}>
        <EVGrid className={classes.consumptionRow}>
          <EVGrid className={classes.consumptionInfo}>
            <EVGrid className={classes.consumptionInfoDetails}>
              <Typography variant="body-600" style={{ color: '#3E649B' }}>
                {subscription.usedQty}
              </Typography>
              <Typography variant="subtitle-600" fontColor="charcoal-700">
                /
                {subscription.includedQty}
              </Typography>
              <Typography variant="subtitle-400" fontColor="charcoal-700" style={{ marginLeft: '4px' }}>
                {t('subscription_card__monthly')}
              </Typography>
            </EVGrid>
            {isOverLimit && (
              <EVGrid className={classes.overageBadge}>
                <Typography variant="label-600" fontColor="salt-100">
                  +
                  {overage}
                  {' '}
                  {t('subscription_card__overage')}
                </Typography>
              </EVGrid>
            )}
          </EVGrid>
          <Link to={ORDER_HISTORY_ROUTE} className={classes.viewOrdersLink}>
            {t('subscription_card__view_orders')}
          </Link>
        </EVGrid>
        <EVGrid className={classes.consumptionProgress}>
          <EVLinearProgress
            classes={{
              bar: isOverLimit ? classes.overageProgressBar : classes.underageProgressBar,
              colorPrimary: classes.consumptionProgressBar,
            }}
            variant="determinate"
            value={(Math.min(subscription.usedQty / subscription.includedQty, 1)) * 100}
          />
        </EVGrid>
      </EVGrid>
    </EVGrid>
  );
}

SubscriptionCard.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    subscriptionSkuName: PropTypes.string.isRequired,
    monthlyFee: PropTypes.number.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    usedQty: PropTypes.number.isRequired,
    includedQty: PropTypes.number.isRequired,
  }).isRequired,
};

export default SubscriptionCard;
